"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import ReactMarkdown from "react-markdown";
import ImaginaryWheel, { SundialAIResults } from "./ImaginaryWheel";
// ─────────────────────────────────────────────────────────────────────────────
// Types — Antonio Nieto-Rodriguez Project Canvas (15 blocks, 4 zones)
// ─────────────────────────────────────────────────────────────────────────────

interface ProjectCanvas {
  // WHY zone
  benefits: string;
  rationale: string;
  // Center
  purpose_passion: string;
  // WHO zone
  executive_sponsor: string;
  governance: string;
  stakeholders: string;
  // WHAT / HOW / WHEN zone
  scope: string;
  risk_management: string;
  human_resources: string;
  time: string;
  cost: string;
  procurement: string;
  quality: string;
  // WHERE zone
  project_driven_organization: string;
  change_management: string;
}

interface EconomyLine {
  label: string;
  amount: number;
  period: string;
}

interface Project {
  project_id: string;
  title: string;
  description: string;
  canvas: Partial<ProjectCanvas>;
  revenues: EconomyLine[];
  costs: EconomyLine[];
  tags: string[];
  sundial_results?: SundialAIResults;
  economy_analysis?: string;
  updated_at?: string;
  owner_username?: string;
  is_public?: boolean;
}

interface Version {
  version_id: string;
  version_label: string;
  saved_at: string;
  title: string;
  description: string;
  canvas: Partial<ProjectCanvas>;
  sundial_results: SundialAIResults;
  revenues: EconomyLine[];
  costs: EconomyLine[];
  economy_analysis?: string;
}

const EMPTY_CANVAS: ProjectCanvas = {
  benefits: "",
  rationale: "",
  purpose_passion: "",
  executive_sponsor: "",
  governance: "",
  stakeholders: "",
  scope: "",
  risk_management: "",
  human_resources: "",
  time: "",
  cost: "",
  procurement: "",
  quality: "",
  project_driven_organization: "",
  change_management: "",
};

const EMPTY_LINE: EconomyLine = { label: "", amount: 0, period: "år" };

// ─────────────────────────────────────────────────────────────────────────────
// API helpers
// ─────────────────────────────────────────────────────────────────────────────

function getAuthHeader(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const token = localStorage.getItem("account_token");
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

async function apiFetch<T = Record<string, unknown>>(
  path: string,
  opts?: RequestInit & { timeoutMs?: number }
): Promise<T> {
  const { timeoutMs, ...fetchOpts } = opts ?? {};
  const r = await fetch(`/api/experiment/${path}`, {
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
    signal: AbortSignal.timeout(timeoutMs ?? 30_000),
    ...fetchOpts,
  });
  if (!r.ok) {
    const err = (await r.json().catch(() => ({ detail: "" }))) as {
      detail?: string;
    };
    throw new Error(err.detail || `API ${r.status}`);
  }
  return r.json() as Promise<T>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Canvas cell component
// ─────────────────────────────────────────────────────────────────────────────

interface CanvasCellProps {
  label: string;
  icon: string;
  hint: string;
  zone: string;
  color: string;
  bg: string;
  border: string;
  value: string;
  onChange: (v: string) => void;
  className?: string;
}

function CanvasCell({
  label,
  icon,
  hint,
  zone,
  color,
  bg,
  border,
  value,
  onChange,
  className = "",
}: CanvasCellProps) {
  return (
    <div
      className={`flex flex-col border p-2 transition-colors ${className}`}
      style={{ backgroundColor: bg, borderColor: border }}
    >
      <div className="flex items-start justify-between gap-1 mb-1 flex-shrink-0">
        <div className="flex items-center gap-1">
          <span className="text-sm leading-none">{icon}</span>
          <span className="text-[11px] font-bold leading-tight" style={{ color }}>
            {label}
          </span>
        </div>
        <span
          className="text-[8px] font-bold uppercase px-1 py-0.5 rounded leading-none whitespace-nowrap flex-shrink-0"
          style={{ backgroundColor: color, color: "#fff", opacity: 0.85 }}
        >
          {zone}
        </span>
      </div>
      <p className="text-[9px] text-gray-400 italic mb-1 leading-snug line-clamp-2">{hint}</p>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Klik for at redigere…"
        className="flex-1 text-[11px] text-gray-700 resize-none bg-transparent focus:outline-none placeholder-gray-300 leading-relaxed"
        rows={4}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Economy line row
// ─────────────────────────────────────────────────────────────────────────────

function EconomyRow({
  line,
  onChange,
  onRemove,
}: {
  line: EconomyLine;
  onChange: (l: EconomyLine) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        placeholder="Beskrivelse"
        value={line.label}
        onChange={(e) => onChange({ ...line, label: e.target.value })}
        className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none"
      />
      <input
        type="number"
        placeholder="0"
        value={line.amount || ""}
        onChange={(e) =>
          onChange({ ...line, amount: parseFloat(e.target.value) || 0 })
        }
        className="w-28 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none text-right"
      />
      <select
        value={line.period}
        onChange={(e) => onChange({ ...line, period: e.target.value })}
        className="w-24 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none"
      >
        <option value="år">kr/år</option>
        <option value="måned">kr/mdr</option>
        <option value="engang">kr engang</option>
      </select>
      <button
        onClick={onRemove}
        className="p-1.5 text-gray-400 hover:text-red-500 rounded"
        title="Slet linje"
      >
        ✕
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Zone colour palette
// ─────────────────────────────────────────────────────────────────────────────

const Z = {
  WHY:   { zone: "HVORFOR", color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  WHO:   { zone: "HVEM",    color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  CORE:  { zone: "FORMÅL",  color: "#7b2d8b", bg: "#faf5ff", border: "#e9d5ff" },
  WHAT:  { zone: "HVAD",    color: "#065f46", bg: "#ecfdf5", border: "#a7f3d0" },
  WHERE: { zone: "HVOR",    color: "#4c1d95", bg: "#f5f3ff", border: "#ddd6fe" },
};

// ─────────────────────────────────────────────────────────────────────────────
// SourcesList — interactive "Påvirket af" tags with hover tooltip + expand
// ─────────────────────────────────────────────────────────────────────────────

type DocSource = { doc_title: string; doc_id: string; reason?: string };

function SourcesList({
  sources,
  onNavigateToPdf,
}: {
  sources: DocSource[];
  onNavigateToPdf?: (id: string, title: string) => void;
}) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [hoveredIdx, setHoveredIdx]   = useState<number | null>(null);

  return (
    <div className="flex flex-col gap-1.5 text-[11px]">
      <div className="flex flex-wrap items-center gap-1.5 text-gray-400">
        <span className="shrink-0">&#128218; Påvirket af:</span>
        {sources.map((s, i) => (
          <div key={i} className="relative">
            <button
              className={`rounded px-1.5 py-0.5 font-medium transition-colors flex items-center gap-0.5 ${
                expandedIdx === i
                  ? "bg-purple-100 text-purple-700"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
              onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {s.doc_title}
              {s.reason && <span className="text-[9px] opacity-40 ml-0.5">▾</span>}
            </button>
            {/* Hover tooltip */}
            {hoveredIdx === i && expandedIdx !== i && s.reason && (
              <div
                className="absolute bottom-full left-0 mb-1.5 z-50 bg-gray-900 text-white text-[10px] rounded-lg p-2 shadow-xl pointer-events-none"
                style={{ width: 230 }}
              >
                <p className="font-semibold mb-1 text-gray-300 text-[9px] uppercase tracking-wide">
                  Kontekst der påvirkede resultatet:
                </p>
                <p className="leading-relaxed opacity-90 line-clamp-4">{s.reason}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Expanded detail panel */}
      {expandedIdx !== null && sources[expandedIdx] && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-700">
          <p className="font-semibold text-gray-500 mb-1.5 text-[10px] uppercase tracking-wide">
            📄 {sources[expandedIdx].doc_title} — Kontekst der påvirkede resultatet:
          </p>
          {sources[expandedIdx].reason && (
            <p className="leading-relaxed text-gray-600 mb-2.5 border-l-2 border-purple-300 pl-2 italic">
              {sources[expandedIdx].reason}
            </p>
          )}
          {onNavigateToPdf && sources[expandedIdx].doc_id && (
            <button
              onClick={() => {
                onNavigateToPdf(sources[expandedIdx].doc_id, sources[expandedIdx].doc_title);
                setExpandedIdx(null);
              }}
              className="text-[11px] px-2.5 py-1.5 rounded-lg text-white font-medium transition-opacity hover:opacity-80"
              style={{ backgroundColor: "#2d6a4f" }}
            >
              📄 Åbn i Klimadokumenter →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main ExperimentSection component
// ─────────────────────────────────────────────────────────────────────────────

const ACCENT = "#7b2d8b";

export default function ExperimentSection({ onNavigateToPdf }: { onNavigateToPdf?: (id: string, title: string) => void } = {}) {
  // ─ Auth state (read from localStorage) ───────────────────────────────────────
  const [authUser, setAuthUser] = useState<{ username: string; role: string } | null>(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [hasPaidSubscription, setHasPaidSubscription] = useState(false);

  // ─ Project state ──────────────────────────────────────────────────
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);

  const [title, setTitle] = useState("Nyt projekt");
  const [description, setDescription] = useState("");
  const [canvas, setCanvas] = useState<ProjectCanvas>({ ...EMPTY_CANVAS });
  const [revenues, setRevenues] = useState<EconomyLine[]>([]);
  const [costs, setCosts] = useState<EconomyLine[]>([]);

  const [tab, setTab] = useState<"canvas" | "economy" | "sundial">("canvas");
  const [sundialResults, setSundialResults] = useState<SundialAIResults>({});
  const [canvasLoading, setCanvasLoading] = useState(false);
  const [streamTokenCount, setStreamTokenCount] = useState(0);
  const [economyLoading, setEconomyLoading] = useState(false);
  const [economyAnalysis, setEconomyAnalysis] = useState("");
  const [canvasSources, setCanvasSources] = useState<DocSource[]>([]);
  const [sundialSources, setSundialSources] = useState<DocSource[]>([]);
  const [economySummary, setEconomySummary] = useState<{
    total_revenue: number; total_cost: number; net: number; profitable: boolean;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [error, setError] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  // Autosave
  const [autosaveStatus, setAutosaveStatus] = useState<"" | "saving" | "saved">("saved");
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressAutosaveRef = useRef(true); // suppress on initial mount
  const activeProjectIdRef = useRef<string | null>(null);
  const latestPayloadRef = useRef({ title, description, canvas, revenues, costs, sundialResults, economyAnalysis });

  // Dirty / unsaved-changes tracking
  const [isDirty, setIsDirty] = useState(false);

  // Save visibility modal
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Version history
  const [versions, setVersions] = useState<Version[]>([]);
  const [showVersions, setShowVersions] = useState(false);
  const [versionRefreshKey, setVersionRefreshKey] = useState(0);

  const loadProjects = useCallback(async () => {
    try {
      const data = await apiFetch<{ projects?: Project[] }>("projects");
      setProjects(data.projects || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  // Read auth from localStorage and fetch user preferences
  useEffect(() => {
    const raw = localStorage.getItem("account_user");
    if (raw) {
      try {
        const u = JSON.parse(raw) as { username: string; role: string };
        setAuthUser(u);
        // Fetch user profile to get auto_save_enabled and subscription status
        const token = localStorage.getItem("account_token");
        if (token) {
          fetch("/api/users/me", { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : null)
            .then((profile) => {
              if (profile) {
                setAutoSaveEnabled(!!profile.auto_save_enabled);
                setHasPaidSubscription(!!profile.odoo_partner_id);
              }
            })
            .catch(() => {});
        }
      } catch { /* ignore malformed */ }
    }
    const onStorage = () => {
      const raw2 = localStorage.getItem("account_user");
      setAuthUser(raw2 ? JSON.parse(raw2) : null);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Warn before browser close/refresh when there are unsaved changes
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Keep payload ref always up-to-date (runs every render)
  useEffect(() => {
    latestPayloadRef.current = { title, description, canvas, revenues, costs, sundialResults, economyAnalysis };
  });
  useEffect(() => { activeProjectIdRef.current = activeProjectId; }, [activeProjectId]);

  // Core autosave executor — reads from refs to avoid stale closures
  const execAutosave = useCallback(async (label: string, overrides?: Partial<typeof latestPayloadRef.current>) => {
    const data = { ...latestPayloadRef.current, ...overrides };
    if (!data.title.trim()) return;
    setAutosaveStatus("saving");
    const payload = {
      title: data.title, description: data.description, canvas: data.canvas,
      revenues: data.revenues, costs: data.costs, tags: [],
      sundial_results: data.sundialResults, economy_analysis: data.economyAnalysis,
      version_label: label,
      is_public: true,  // auto-saves are always public
    };
    try {
      const pid = activeProjectIdRef.current;
      if (pid) {
        await apiFetch(`projects/${pid}`, { method: "PUT", body: JSON.stringify(payload) });
      } else {
        const created = await apiFetch<{ project_id: string }>("projects", { method: "POST", body: JSON.stringify(payload) });
        setActiveProjectId(created.project_id);
        activeProjectIdRef.current = created.project_id;
        await loadProjects();
      }
      setAutosaveStatus("saved");
      setIsDirty(false);
      setTimeout(() => setAutosaveStatus(""), 3000);
      setVersionRefreshKey((k) => k + 1);
    } catch {
      setAutosaveStatus("");
    }
  }, [loadProjects]);

  // Schedule an autosave (debounced for user edits, immediate for AI)
  const scheduleAutosave = useCallback((label: string, delay = 2500, overrides?: Partial<typeof latestPayloadRef.current>) => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => execAutosave(label, overrides), delay);
  }, [execAutosave]);

  // Debounce autosave on canvas / sundial / title / description changes
  useEffect(() => {
    if (suppressAutosaveRef.current) {
      suppressAutosaveRef.current = false;
      // Return a cleanup that resets the flag so React StrictMode's simulated
      // unmount+remount doesn't cause a false dirty on initial load.
      return () => { suppressAutosaveRef.current = true; };
    }
    // Mark as dirty whenever content changes
    setIsDirty(true);
    // Only trigger auto-save if the user has the preference enabled AND is logged in
    if (!autoSaveEnabled || !authUser) return;
    scheduleAutosave("Bruger redigering");
    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas, sundialResults, title, description]);

  // Load version list when panel is opened or after a save
  const loadVersions = useCallback(async (pid: string) => {
    try {
      const data = await apiFetch<{ versions: Version[] }>(`projects/${pid}/versions`);
      setVersions(data.versions || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (activeProjectId && showVersions) loadVersions(activeProjectId);
  }, [activeProjectId, showVersions, versionRefreshKey, loadVersions]);

  const selectProject = (p: Project) => {
    if (isDirty && !confirm("Du har ikke-gemte ændringer. Vil du kassere dem?")) return;
    suppressAutosaveRef.current = true;
    setActiveProjectId(p.project_id);
    setTitle(p.title);
    setDescription(p.description);
    setCanvas({ ...EMPTY_CANVAS, ...(p.canvas || {}) });
    setRevenues(p.revenues || []);
    setCosts(p.costs || []);
    setSundialResults(p.sundial_results || {});
    setEconomyAnalysis(p.economy_analysis || "");
    setEconomySummary(null); setError(""); setSaveMsg("");
    setShowVersions(false);
    setIsDirty(false);
  };

  const newProject = () => {
    if (isDirty && !confirm("Du har ikke-gemte ændringer. Vil du kassere dem?")) return;
    suppressAutosaveRef.current = true;
    setActiveProjectId(null);
    setTitle("Nyt projekt");
    setDescription("");
    setCanvas({ ...EMPTY_CANVAS });
    setRevenues([]); setCosts([]);
    setSundialResults({});
    setEconomyAnalysis(""); setEconomySummary(null); setSaveMsg(""); setError("");
    setShowVersions(false);
    setIsDirty(false);
  };

  const handleRestoreVersion = async (v: Version) => {
    if (!activeProjectId) return;
    try {
      await apiFetch(`projects/${activeProjectId}/restore/${v.version_id}`, { method: "POST" });
      suppressAutosaveRef.current = true;
      setTitle(v.title || "");
      setDescription(v.description || "");
      setCanvas({ ...EMPTY_CANVAS, ...(v.canvas || {}) });
      setSundialResults(v.sundial_results || {});
      setRevenues(v.revenues || []);
      setCosts(v.costs || []);
      setEconomyAnalysis(v.economy_analysis || "");
      setIsDirty(false);
      setSaveMsg("✅ Version gendannet");
      setTimeout(() => setSaveMsg(""), 3000);
      setShowVersions(false);
      setVersionRefreshKey((k) => k + 1);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Gendannelse fejlede");
    }
  };

  const handleGenerateCanvas = async () => {
    if (!title.trim()) { setError("Giv projektet en titel først"); return; }
    setCanvasLoading(true);
    setStreamTokenCount(0);
    setError("");
    try {
      const res = await fetch("/api/experiment/canvas/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, description, existing_canvas: canvas }),
        signal: AbortSignal.timeout(600_000),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` })) as { detail?: string };
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let tokenCount = 0;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // SSE lines: "data: <json>\n\n"
        const parts = buf.split("\n\n");
        buf = parts.pop() ?? "";
        for (const part of parts) {
          const line = part.replace(/^data:\s*/, "").trim();
          if (!line || line.startsWith(":")) continue;  // skip empty / SSE comments
          try {
            const evt = JSON.parse(line) as { token?: string; canvas?: Partial<ProjectCanvas>; done?: boolean; error?: string; sources?: DocSource[] };
            if (evt.error) throw new Error(evt.error);
            if (evt.token) {
              tokenCount += 1;
              setStreamTokenCount(tokenCount);
            }
            if (evt.done && evt.canvas) {
              if (evt.sources) setCanvasSources(evt.sources);
              const filled = evt.canvas;
              const baseCanvas = latestPayloadRef.current.canvas as ProjectCanvas;
              const mergedCanvas: ProjectCanvas = { ...baseCanvas };
              for (const [k, v] of Object.entries(filled)) {
                if (!v) continue;
                const str = Array.isArray(v)
                  ? (v as string[]).map((item) => `• ${item}`).join("\n")
                  : typeof v === "string" ? v : String(v);
                (mergedCanvas as unknown as Record<string, string>)[k] = str;
              }
              suppressAutosaveRef.current = true; // skip debounce
              setCanvas(mergedCanvas);
              // Only AI-triggered auto-save if user opted in AND is logged in
              if (autoSaveEnabled && authUser) {
                scheduleAutosave("✨ AI Canvas", 0, { canvas: mergedCanvas });
              } else {
                setIsDirty(true);
              }
            }
          } catch (parseErr) {
            if (parseErr instanceof Error && parseErr.message !== "Unexpected end of JSON input") {
              throw parseErr;
            }
          }
        }
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "AI fejl");
    } finally {
      setCanvasLoading(false);
      setStreamTokenCount(0);
    }
  };

  const handleAnalyzeEconomy = async () => {
    setEconomyLoading(true); setEconomyAnalysis(""); setError("");
    try {
      const data = await apiFetch<{
        analysis?: string;
        summary?: { total_revenue: number; total_cost: number; net: number; profitable: boolean };
      }>("economy/analyze", {
        method: "POST",
        body: JSON.stringify({ title, description, revenues, costs }),
        timeoutMs: 180_000,
      });
      const analysis = data.analysis || "";
      setEconomyAnalysis(analysis);
      setEconomySummary(data.summary || null);
      // Auto-save analysis to DB so it appears in PDF report — only if user opted in
      if (analysis && activeProjectId && autoSaveEnabled && authUser) {
        const payload = { title, description, canvas, revenues, costs, tags: [], sundial_results: sundialResults, economy_analysis: analysis, is_public: true };
        try {
          await apiFetch(`projects/${activeProjectId}`, { method: "PUT", body: JSON.stringify(payload) });
          await loadProjects();
        } catch { /* silent */ }
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "AI fejl");
    } finally { setEconomyLoading(false); }
  };

  // Show save modal — triggered by clicking "💾 Gem projekt"
  const handleSaveClick = () => {
    if (!authUser) { setError("Log ind for at gemme dit projekt"); return; }
    setShowSaveModal(true);
  };

  // Perform the actual save with chosen visibility
  const handleSave = async (isPublic: boolean) => {
    setShowSaveModal(false);
    setSaving(true); setSaveMsg(""); setError("");
    const payload = {
      title, description, canvas, revenues, costs, tags: [],
      sundial_results: sundialResults, economy_analysis: economyAnalysis,
      is_public: isPublic,
    };
    try {
      if (activeProjectId) {
        await apiFetch(`projects/${activeProjectId}`, { method: "PUT", body: JSON.stringify(payload) });
        setSaveMsg("✅ Gemt");
      } else {
        const created = await apiFetch<{ project_id: string }>("projects", {
          method: "POST", body: JSON.stringify(payload),
        });
        setActiveProjectId(created.project_id);
        setSaveMsg("✅ Oprettet");
      }
      setIsDirty(false);
      await loadProjects();
      setVersionRefreshKey((k) => k + 1);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Gem fejl");
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(""), 3000);
    }
  };

  const handleDelete = async () => {
    if (!activeProjectId) return;
    if (!authUser) { setError("Log ind for at slette projekter"); return; }
    if (!confirm(`Slet "${title}"?`)) return;
    try {
      await apiFetch(`projects/${activeProjectId}`, { method: "DELETE" });
      newProject(); loadProjects();
    } catch (e: unknown) { setError(e instanceof Error ? e.message : "Slet fejl"); }
  };

  const handleDownloadPDF = async () => {
    if (!activeProjectId) return;
    setPdfLoading(true);
    setError("");
    try {
      const res = await fetch(`/api/experiment/projects/${activeProjectId}/report`, {
        signal: AbortSignal.timeout(120_000),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({ detail: "" }))) as { detail?: string };
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `rapport_${title.replace(/[^a-zA-Z0-9_-]/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "PDF fejl");
    } finally {
      setPdfLoading(false);
    }
  };

  const totalRevenue = revenues.reduce((s, r) => s + (r.amount || 0), 0);
  const totalCost = costs.reduce((s, c) => s + (c.amount || 0), 0);
  const net = totalRevenue - totalCost;

  const set = (k: keyof ProjectCanvas) => (v: string) =>
    setCanvas((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="flex flex-col gap-4 min-h-0">

      {/* ── Save visibility modal ── */}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <h3 className="font-semibold text-gray-900 text-base mb-1">Gem projekt</h3>
            <p className="text-sm text-gray-500 mb-5">
              Vælg synlighed — dit brugernavn vises som opretter.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => handleSave(true)}
                className="flex items-start gap-3 p-3.5 rounded-xl border-2 border-gray-200 hover:border-purple-400 hover:bg-purple-50 text-left transition-colors"
              >
                <span className="text-xl mt-0.5">🌍</span>
                <div>
                  <p className="font-medium text-gray-900 text-sm">Offentlig</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Gratis · Alle kan se projektet · Dit brugernavn ({authUser?.username}) er synligt som opretter
                  </p>
                </div>
              </button>
              <button
                onClick={() => {
                  if (!hasPaidSubscription) {
                    alert("Privat lagring kræver et betalt abonnement. Opgrader under din profil.");
                    return;
                  }
                  handleSave(false);
                }}
                className={`flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-colors ${
                  hasPaidSubscription
                    ? "border-gray-200 hover:border-purple-400 hover:bg-purple-50"
                    : "border-gray-100 opacity-50 cursor-not-allowed"
                }`}
              >
                <span className="text-xl mt-0.5">🔒</span>
                <div>
                  <p className="font-medium text-gray-900 text-sm">
                    Kun for mig
                    {!hasPaidSubscription && (
                      <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold uppercase">Kræver abonnement</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Betalt abonnement · Kun synlig for dig · Skjult for andre
                  </p>
                </div>
              </button>
            </div>
            <button
              onClick={() => setShowSaveModal(false)}
              className="mt-4 w-full py-2 text-sm text-gray-500 hover:text-gray-700"
            >
              Annuller
            </button>
          </div>
        </div>
      )}

      {/* ── Unsaved changes banner ── */}
      {isDirty && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-xl text-sm text-amber-800">
          <span>⚠️</span>
          <span className="flex-1">Du har ikke-gemte ændringer.</span>
          {authUser ? (
            <button
              onClick={handleSaveClick}
              className="text-xs font-semibold px-2.5 py-1 rounded-lg text-white"
              style={{ backgroundColor: ACCENT }}
            >
              Gem nu
            </button>
          ) : (
            <a href="/account" className="text-xs font-semibold text-purple-700 underline">Log ind for at gemme</a>
          )}
        </div>
      )}

      {/* ── Top bar ── */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={activeProjectId || ""}
          onChange={(e) => {
            const p = projects.find((x) => x.project_id === e.target.value);
            if (p) selectProject(p); else newProject();
          }}
          className="flex-1 min-w-0 max-w-xs px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none"
        >
          <option value="">— Nyt projekt —</option>
          {projects.map((p) => (
            <option key={p.project_id} value={p.project_id}>{p.title}</option>
          ))}
        </select>
        {/* Only logged-in users can create a new project */}
        {authUser && (
          <button onClick={newProject} className="px-3 py-2 text-sm border border-gray-200 rounded-xl hover:bg-gray-50">
            ➕ Nyt
          </button>
        )}
        <button
          onClick={handleSaveClick} disabled={saving}
          className="px-4 py-2 text-sm text-white rounded-xl disabled:opacity-50"
          style={{ backgroundColor: ACCENT }}
        >
          {saving ? "⏳ Gemmer…" : authUser ? "💾 Gem projekt" : "🔐 Log ind for at gemme"}
        </button>
        {/* Delete: only project owner or admin */}
        {activeProjectId && authUser && (() => {
          const proj = projects.find((p) => p.project_id === activeProjectId);
          const canDelete = authUser.role === "admin" ||
            !proj?.owner_username ||  // legacy unowned — show button, backend enforces admin-only
            proj.owner_username === authUser.username;
          return canDelete ? (
            <button onClick={handleDelete} className="px-3 py-2 text-sm text-red-600 border border-red-200 rounded-xl hover:bg-red-50">
              🗑
            </button>
          ) : null;
        })()}
        {activeProjectId && (
          <button
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
            title="Download samlet PDF-rapport: Project Canvas + Økonomi + Imagination Sundial"
          >
            {pdfLoading
              ? <><span className="animate-spin inline-block text-xs">⚙️</span> <span className="hidden sm:inline">Genererer…</span></>
              : <>📄 <span className="hidden sm:inline">Download rapport</span><span className="sm:hidden">PDF</span></>}
          </button>
        )}
        {/* Autosave status */}
        {autosaveStatus === "saving" && (
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg>
            Autogem…
          </span>
        )}
        {autosaveStatus === "saved" && (
          <span className="text-xs text-green-600">✓ Autogemt</span>
        )}
        {saveMsg && <span className="text-xs text-green-700 font-medium">{saveMsg}</span>}
        {error && <span className="text-xs text-red-600">{error}</span>}

        {/* Version history */}
        {activeProjectId && (
          <div className="relative ml-auto">
            <button
              onClick={() => setShowVersions((v) => !v)}
              className={`px-3 py-2 text-sm border rounded-xl flex items-center gap-1.5 transition-colors ${showVersions ? "border-purple-300 bg-purple-50 text-purple-700" : "border-gray-200 hover:bg-gray-50 text-gray-600"}`}
              title="Versionshistorik"
            >
              🕐 <span className="hidden sm:inline text-xs">Versioner</span>
              {versions.length > 0 && (
                <span className="text-[10px] bg-purple-100 text-purple-600 rounded-full px-1.5 py-0.5 font-medium">{versions.length}</span>
              )}
            </button>
            {showVersions && (
              <div className="absolute top-full right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-2xl z-40 p-3 min-w-72 max-h-80 overflow-y-auto">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Versionshistorik</p>
                {versions.length === 0 ? (
                  <p className="text-xs text-gray-400 py-2 text-center">Ingen versioner endnu</p>
                ) : (
                  <div className="flex flex-col divide-y divide-gray-100">
                    {versions.map((v) => (
                      <div key={v.version_id} className="flex items-center justify-between gap-2 py-2">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-700 truncate">{v.version_label}</p>
                          <p className="text-[10px] text-gray-400">
                            {new Date(v.saved_at).toLocaleDateString("da-DK", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRestoreVersion(v)}
                          className="text-[11px] px-2 py-1 rounded-lg border border-purple-200 text-purple-700 hover:bg-purple-50 shrink-0 whitespace-nowrap"
                        >
                          Gendan
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Title & description ── */}
      <input
        type="text" value={title} onChange={(e) => setTitle(e.target.value)}
        className="w-full px-3 py-2 text-sm font-semibold border border-gray-200 rounded-xl focus:outline-none"
        placeholder="Projekttitel"
      />
      <textarea
        value={description} onChange={(e) => setDescription(e.target.value)}
        placeholder="Kort beskrivelse af projektet (bruges af AI til at udfylde Project Canvas og analysere økonomi)…"
        rows={2}
        className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none resize-none text-gray-700 placeholder-gray-400"
      />

      {/* ── Tab bar ── */}
      <div className="flex gap-1 border-b border-gray-200">
        {(["canvas", "economy", "sundial"] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              tab === t
                ? "border-t border-l border-r border-gray-200 bg-white -mb-px text-gray-900"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t === "canvas" ? "🗂️ Project Canvas" : t === "economy" ? "📊 Projektøkonomi" : "🌅 Imagination Sundial"}
          </button>
        ))}
      </div>

      {/* ════════════════════════ PROJECT CANVAS TAB ════════════════════════ */}
      {tab === "canvas" && (
        <div className="flex flex-col gap-3">
          {/* AI button */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleGenerateCanvas} disabled={canvasLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white rounded-xl disabled:opacity-50"
              style={{ backgroundColor: ACCENT }}
            >
              {canvasLoading
              ? <><span className="animate-spin inline-block">⚙️</span> AI genererer… {streamTokenCount > 0 && <span className="opacity-70">({streamTokenCount} tokens)</span>}</>
                : <>✨ AI: Generer Project Canvas</>}
            </button>
            <span className="text-xs text-gray-400">
              AI udfylder alle 15 blokke baseret på titel og beskrivelse
            </span>
          </div>

          {/*
            Grid layout — 5 columns × 4 rows:
            col:   1               2                3             4              5
            row1: Benefits       Purpose&Passion  Exec.Sponsor  Governance     Stakeholders
            row2: Benefits       Purpose&Passion  Scope         Risk Mgmt      Human Res.
            row3: Rationale      Time             Cost          Procurement    Quality
            row4: [Proj.Org — cols 1-3]                         [Change Mgmt — cols 4-5]
          */}
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <div
            className="grid rounded-xl overflow-hidden border-2 shadow-sm"
            style={{
              gridTemplateColumns: "repeat(5, 1fr)",
              gridTemplateRows: "repeat(4, auto)",
              borderColor: "#d1d5db",
              gap: "1px",
              backgroundColor: "#d1d5db",
              minWidth: "720px",
            }}
          >
            <CanvasCell label="Gevinster" icon="🎯"
              hint="Hvad er de forventede gevinster og resultater af projektet?"
              {...Z.WHY} value={canvas.benefits} onChange={set("benefits")}
              className="col-start-1 row-start-1 row-span-2" />

            <CanvasCell label="Formål & Passion" icon="💜"
              hint="Den drivende motivation og overordnede vision — hvorfor brænder vi for det?"
              {...Z.CORE} value={canvas.purpose_passion} onChange={set("purpose_passion")}
              className="col-start-2 row-start-1 row-span-2" />

            <CanvasCell label="Executive Sponsor" icon="👑"
              hint="Hvem er overordnet ansvarlig og stiller ressourcer til rådighed?"
              {...Z.WHO} value={canvas.executive_sponsor} onChange={set("executive_sponsor")}
              className="col-start-3 row-start-1" />
            <CanvasCell label="Governance" icon="⚖️"
              hint="Styregruppe, roller og beslutningsstruktur — hvem bestemmer hvad?"
              {...Z.WHO} value={canvas.governance} onChange={set("governance")}
              className="col-start-4 row-start-1" />
            <CanvasCell label="Interessenter" icon="🤝"
              hint="Nøgleinteressenter — hvem påvirkes og skal engageres?"
              {...Z.WHO} value={canvas.stakeholders} onChange={set("stakeholders")}
              className="col-start-5 row-start-1" />

            <CanvasCell label="Omfang" icon="📋"
              hint="Leverancer og afgrænsning — hvad producerer projektet konkret?"
              {...Z.WHAT} value={canvas.scope} onChange={set("scope")}
              className="col-start-3 row-start-2" />
            <CanvasCell label="Risikostyring" icon="⚠️"
              hint="Identificerede risici og plan B — hvad kan gå galt?"
              {...Z.WHAT} value={canvas.risk_management} onChange={set("risk_management")}
              className="col-start-4 row-start-2" />
            <CanvasCell label="HR & Team" icon="👥"
              hint="Kompetencer og motivation — hvem skal vi bruge og hvordan engagerer vi dem?"
              {...Z.WHAT} value={canvas.human_resources} onChange={set("human_resources")}
              className="col-start-5 row-start-2" />

            <CanvasCell label="Rationale & Business Case" icon="💡"
              hint="Hvorfor gør vi det nu? Hvilke behov løser projektet? Baggrund og begrundelse"
              {...Z.WHY} value={canvas.rationale} onChange={set("rationale")}
              className="col-start-1 row-start-3" />
            <CanvasCell label="Tid" icon="🗓️"
              hint="Hvornår afsluttes projektet? Tidsplan og milepæle"
              {...Z.WHAT} value={canvas.time} onChange={set("time")}
              className="col-start-2 row-start-3" />
            <CanvasCell label="Økonomi" icon="💰"
              hint="Hvad koster projektet? Ressourcebehov og budget"
              {...Z.WHAT} value={canvas.cost} onChange={set("cost")}
              className="col-start-3 row-start-3" />
            <CanvasCell label="Indkøb" icon="🔧"
              hint="Hvordan styrer vi eksterne leverandører og bidragydere?"
              {...Z.WHAT} value={canvas.procurement} onChange={set("procurement")}
              className="col-start-4 row-start-3" />
            <CanvasCell label="Kvalitet" icon="✅"
              hint="Hvordan sikrer vi at leverancerne opfylder kvalitetskravene?"
              {...Z.WHAT} value={canvas.quality} onChange={set("quality")}
              className="col-start-5 row-start-3" />

            <CanvasCell label="Projektdrevet Organisation" icon="🏗️"
              hint="Er organisationens kultur, struktur og prioriteter klar til et projektdrevet arbejde? (Kultur · Struktur · Prioriteter · Kompetencer)"
              {...Z.WHERE} value={canvas.project_driven_organization} onChange={set("project_driven_organization")}
              className="col-start-1 col-span-3 row-start-4" />
            <CanvasCell label="Forandringsledelse" icon="🔄"
              hint="Hvordan engagerer vi interessenter og fjerner barrierer for forandring?"
              {...Z.WHERE} value={canvas.change_management} onChange={set("change_management")}
              className="col-start-4 col-span-2 row-start-4" />
          </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-[11px] text-gray-400 pt-1 items-center">
            <span className="font-medium text-gray-500">Project Canvas — Antonio Nieto-Rodriguez</span>
            <a href="https://www.youtube.com/watch?v=LtamYbk4FVQ" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-600 underline">The Project Canvas by Antonio Nieto Rodriguez</a>
            <a href="https://www.youtube.com/watch?v=t577PGkoYQU" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-600 underline">Your Projects, Your Future | Lee Newman &amp; Antonio Nieto-Rodríguez</a>
            <div className="flex gap-3 flex-wrap">
              {Object.values(Z).map((z) => (
                <span key={z.zone} className="flex items-center gap-1">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm"
                    style={{ backgroundColor: z.bg, border: `1px solid ${z.border}` }} />
                  <span style={{ color: z.color }} className="font-medium">{z.zone}</span>
                </span>
              ))}
            </div>
            <span className="ml-auto">Klik på en blok for at redigere</span>
          </div>
          {canvasSources.length > 0 && (
            <SourcesList sources={canvasSources} onNavigateToPdf={onNavigateToPdf} />
          )}
        </div>
      )}

      {/* ════════════════════════ ECONOMY TAB ════════════════════════ */}
      {tab === "economy" && (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Revenues */}
            <div className="bg-green-50 rounded-xl border border-green-200 p-4">
              <h4 className="font-semibold text-green-800 text-sm mb-3">💰 Indtægter</h4>
              <div className="flex flex-col gap-2 mb-3">
                {revenues.map((r, i) => (
                  <EconomyRow key={i} line={r}
                    onChange={(l) => setRevenues((prev) => prev.map((x, j) => j === i ? l : x))}
                    onRemove={() => setRevenues((prev) => prev.filter((_, j) => j !== i))} />
                ))}
              </div>
              <button onClick={() => setRevenues((prev) => [...prev, { ...EMPTY_LINE }])}
                className="text-xs text-green-700 border border-green-300 rounded-lg px-3 py-1.5 hover:bg-green-100">
                ＋ Tilføj indtægt
              </button>
              <div className="mt-3 pt-3 border-t border-green-300 flex justify-between text-sm font-semibold text-green-900">
                <span>Total</span>
                <span>{totalRevenue.toLocaleString("da-DK")} kr</span>
              </div>
            </div>

            {/* Costs */}
            <div className="bg-red-50 rounded-xl border border-red-200 p-4">
              <h4 className="font-semibold text-red-800 text-sm mb-3">💸 Udgifter</h4>
              <div className="flex flex-col gap-2 mb-3">
                {costs.map((c, i) => (
                  <EconomyRow key={i} line={c}
                    onChange={(l) => setCosts((prev) => prev.map((x, j) => j === i ? l : x))}
                    onRemove={() => setCosts((prev) => prev.filter((_, j) => j !== i))} />
                ))}
              </div>
              <button onClick={() => setCosts((prev) => [...prev, { ...EMPTY_LINE }])}
                className="text-xs text-red-700 border border-red-300 rounded-lg px-3 py-1.5 hover:bg-red-100">
                ＋ Tilføj udgift
              </button>
              <div className="mt-3 pt-3 border-t border-red-300 flex justify-between text-sm font-semibold text-red-900">
                <span>Total</span>
                <span>{totalCost.toLocaleString("da-DK")} kr</span>
              </div>
            </div>
          </div>

          {/* Net summary bar */}
          <div className={`rounded-xl p-4 flex items-center justify-between ${net >= 0 ? "bg-green-700 text-white" : "bg-red-600 text-white"}`}>
            <span className="font-semibold">{net >= 0 ? "✅ Netto overskud" : "⚠️ Netto underskud"}</span>
            <span className="text-xl font-bold">{net >= 0 ? "+" : ""}{net.toLocaleString("da-DK")} kr</span>
          </div>

          <button onClick={handleAnalyzeEconomy} disabled={economyLoading}
            className="self-start flex items-center gap-2 px-5 py-2.5 text-sm text-white rounded-xl disabled:opacity-50"
            style={{ backgroundColor: ACCENT }}
          >
            {economyLoading
              ? <><span className="animate-spin">⚙️</span> AI analyserer…</>
              : <>✨ AI: Analysér projektøkonomi</>}
          </button>

          {economyAnalysis && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-purple-600 text-lg">🤖</span>
                <span className="font-semibold text-purple-900 text-sm">AI Analyse</span>
                {economySummary && (
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-medium ${
                    economySummary.profitable ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                  }`}>
                    {economySummary.profitable ? "Rentabel" : "Ikke rentabel"}
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-700 leading-relaxed prose prose-sm prose-purple max-w-none">
                <ReactMarkdown>{economyAnalysis}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════ IMAGINATION SUNDIAL TAB ═══════════════════ */}
      {tab === "sundial" && (
        <div className="flex flex-col gap-2">
          <ImaginaryWheel
            projectTitle={title}
            projectDesc={description}
            aiResults={sundialResults}
            onAIFill={setSundialResults}
            onAISources={setSundialSources}
            onAIFillComplete={async (finalResults) => {
              setSundialResults(finalResults);
              suppressAutosaveRef.current = true; // skip debounce
              if (autoSaveEnabled && authUser) {
                scheduleAutosave("🌅 AI Sundial", 0, { sundialResults: finalResults });
              } else {
                setIsDirty(true);
              }
            }}
          />
          {sundialSources.length > 0 && (
            <SourcesList sources={sundialSources} onNavigateToPdf={onNavigateToPdf} />
          )}
        </div>
      )}
    </div>
  );
}
